import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  ogImage?: string;
  canonicalUrl?: string;
  type?: 'website' | 'article';
  author?: string;
  publishedTime?: string;
}

const SEOHead = ({ title, description, keywords, ogImage, canonicalUrl, type = 'website', author, publishedTime }: SEOProps) => {
  const location = useLocation();
  const { language, getCurrentPath } = useLanguage();
  const baseUrl = window.location.origin;
  const fullUrl = canonicalUrl || `${baseUrl}${location.pathname}`;
  const defaultOgImage = 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1200';

  useEffect(() => {
    // Set title
    document.title = title;

    // Helper function to set meta tag
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    // Basic meta tags
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);

    // Open Graph tags
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:url', fullUrl, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:image', ogImage || defaultOgImage, true);
    setMetaTag('og:image:width', '1200', true);
    setMetaTag('og:image:height', '630', true);
    setMetaTag('og:image:alt', title, true);
    setMetaTag('og:locale', language === 'ro' ? 'ro_RO' : 'en_US', true);
    setMetaTag('og:locale:alternate', language === 'ro' ? 'en_US' : 'ro_RO', true);
    setMetaTag('og:site_name', 'ControlMax', true);

    // Article-specific Open Graph tags
    if (type === 'article') {
      if (author) {
        setMetaTag('article:author', author, true);
      }
      if (publishedTime) {
        setMetaTag('article:published_time', publishedTime, true);
      }
      setMetaTag('article:section', 'Business', true);
    }

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage || defaultOgImage);

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', fullUrl);

    // Hreflang tags for alternate languages
    const currentPath = getCurrentPath();
    const roUrl = `${baseUrl}${currentPath}`;
    const enUrl = `${baseUrl}/en${currentPath}`;

    // Remove existing hreflang tags
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

    // Add Romanian hreflang
    const roLink = document.createElement('link');
    roLink.setAttribute('rel', 'alternate');
    roLink.setAttribute('hreflang', 'ro');
    roLink.setAttribute('href', roUrl);
    document.head.appendChild(roLink);

    // Add English hreflang
    const enLink = document.createElement('link');
    enLink.setAttribute('rel', 'alternate');
    enLink.setAttribute('hreflang', 'en');
    enLink.setAttribute('href', enUrl);
    document.head.appendChild(enLink);

    // Add x-default hreflang (points to Romanian as default)
    const xDefaultLink = document.createElement('link');
    xDefaultLink.setAttribute('rel', 'alternate');
    xDefaultLink.setAttribute('hreflang', 'x-default');
    xDefaultLink.setAttribute('href', roUrl);
    document.head.appendChild(xDefaultLink);

  }, [title, description, keywords, ogImage, fullUrl, defaultOgImage, language, getCurrentPath, baseUrl]);

  return null;
};

export default SEOHead;
